import type { BoxMaskRect } from './custom-template';

type Rect4 = readonly [number, number, number, number];

type BakeInsets = { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number };

export interface BakedDecorLayerInput {
  readonly rect: Rect4;
  readonly rotation: number;
  readonly insets: BakeInsets | undefined;
  readonly flipped: boolean;
  readonly masked: boolean;
  readonly ownMaskRect: boolean;
}

interface BakedDecorMaskCarrier {
  readonly maskDataUrl?: string | null;
  readonly maskUrl?: string | null;
  readonly maskFileId?: string | null;
  readonly maskRect?: unknown;
}

/** Признаки отражения и маски для `bakedDecorLayerRect`: поза бокса и носитель его маски. */
export function bakedDecorLayerTraits(
  pose: { readonly flipHorizontal?: boolean; readonly flipVertical?: boolean } | undefined,
  mask: BakedDecorMaskCarrier,
): Pick<BakedDecorLayerInput, 'flipped' | 'masked' | 'ownMaskRect'> {
  return {
    flipped: Boolean(pose?.flipHorizontal || pose?.flipVertical),
    masked: Boolean(mask.maskDataUrl || mask.maskUrl || mask.maskFileId),
    ownMaskRect: Boolean(mask.maskRect),
  };
}

// Поля лежат в осях бокса, а рендер вращает плитку вокруг её центра: центр слоя сдвинут на повёрнутый вектор полей.
function insetShift(insets: BakeInsets, rotation: number): { dx: number; dy: number; cos: number; sin: number } {
  const angle = (rotation * Math.PI) / 180;
  return {
    dx: (insets.left - insets.right) / 2,
    dy: (insets.top - insets.bottom) / 2,
    cos: Math.cos(angle),
    sin: Math.sin(angle),
  };
}

/** Слой внутри плитки с полями эффектов; `undefined` — не сужать: отражённый, повёрнутый с маской (окно осевое). */
export function bakedDecorLayerRect(
  input: BakedDecorLayerInput,
): { readonly rect: [number, number, number, number]; readonly maskRect?: BoxMaskRect } | undefined {
  const { insets, rotation, masked } = input;
  if (!insets || input.flipped) return undefined;
  if (masked && rotation) return undefined;
  const { left, top, right, bottom } = insets;
  const [x, y, width, height] = input.rect;
  const layerWidth = Math.max(1, width - left - right);
  const layerHeight = Math.max(1, height - top - bottom);
  const { dx, dy, cos, sin } = insetShift(insets, rotation);
  const cx = x + width / 2 + dx * cos - dy * sin;
  const cy = y + height / 2 + dx * sin + dy * cos;
  return {
    rect: [cx - layerWidth / 2, cy - layerHeight / 2, layerWidth, layerHeight],
    ...(masked && !input.ownMaskRect && { maskRect: { x, y, width, height } }),
  };
}

/**
 * Плитка вокруг слоя — обратный шаг `bakedDecorLayerRect`; после клампа `max(1, …)` размер плитки не восстановить.
 * Поза — текущая поза бокса: рендер отражает плитку внутри её rect, а потом вращает вокруг центра, поэтому
 * отражение по оси меняет знак сдвига полей по этой оси.
 */
export function bakedDecorTileRect(
  layer: {
    readonly rect: Rect4;
    readonly rotation: number;
    readonly flipHorizontal?: boolean;
    readonly flipVertical?: boolean;
  },
  insets: BakeInsets,
): [number, number, number, number] {
  const [x, y, width, height] = layer.rect;
  const tileWidth = width + insets.left + insets.right;
  const tileHeight = height + insets.top + insets.bottom;
  const shift = insetShift(insets, layer.rotation);
  const { cos, sin } = shift;
  const dx = layer.flipHorizontal ? -shift.dx : shift.dx;
  const dy = layer.flipVertical ? -shift.dy : shift.dy;
  const cx = x + width / 2 - dx * cos + dy * sin;
  const cy = y + height / 2 - dx * sin - dy * cos;
  return [cx - tileWidth / 2, cy - tileHeight / 2, tileWidth, tileHeight];
}

interface BakedMarginsCarrier {
  readonly bakeInsets?: BakeInsets;
  readonly decorSource?: { readonly bakeInsets?: BakeInsets; readonly windowInsets?: BakeInsets };
}

function scaledInsets<T extends BakeInsets>(insets: T, sx: number, sy: number): T {
  return { ...insets, left: insets.left * sx, top: insets.top * sy, right: insets.right * sx, bottom: insets.bottom * sy };
}

/**
 * Поля запечённых эффектов и окна — пиксели страницы вдоль осей бокса (парсер снимает их с позиции слоя), а
 * движок растягивает плитку на rect бокса. Ресайз бокса по его осям в (sx, sy) масштабирует и их: у декора
 * `bakeInsets`, у рамки — отложенные `decorSource.bakeInsets`/`windowInsets`. Сдвиг без смены размера их не
 * трогает. Поза слоя (`decorSource.layerPose`) не масштабируется: слой повёрнутой рамки возвращается своим размером.
 */
export function resizedBakedMargins<P extends BakedMarginsCarrier>(params: P, sx: number, sy: number): P {
  if ((sx === 1 && sy === 1) || !(sx > 0) || !(sy > 0)) return params;
  const { bakeInsets, decorSource } = params;
  const frame = decorSource?.bakeInsets || decorSource?.windowInsets ? decorSource : undefined;
  if (!bakeInsets && !frame) return params;
  return {
    ...params,
    ...(bakeInsets && { bakeInsets: scaledInsets(bakeInsets, sx, sy) }),
    ...(frame && {
      decorSource: {
        ...frame,
        ...(frame.bakeInsets && { bakeInsets: scaledInsets(frame.bakeInsets, sx, sy) }),
        ...(frame.windowInsets && { windowInsets: scaledInsets(frame.windowInsets, sx, sy) }),
      },
    }),
  } as P;
}

/** Масштаб ресайза по осям бокса: новый размер к старому; сторона без длины — 1. */
export function boxResizeScales(
  from: { readonly width: number; readonly height: number },
  to: { readonly width: number; readonly height: number },
): readonly [number, number] {
  return [from.width > 0 ? to.width / from.width : 1, from.height > 0 ? to.height / from.height : 1];
}
